import { useState, useCallback } from "react"
import { Trans, useTranslation } from "react-i18next"
import { downloadExport } from "../../../helpers/downloadExport"
import {
  Badge,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Select,
  NumberInput,
  Button,
  Tooltip,
  Card,
  Modal,
  Stack,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { modals } from "@mantine/modals"
import { DataTable, useDataTable } from "../../../components/DataTable"
import type { Column } from "../../../components/DataTable"
import { useDeleteTravelersCheck, useCheckAgencies, useRequestRedeemForm, useRedeem } from "../hooks/useTravelersChecks"
import { travelersCheckApi } from "../services/travelersCheckApi"
import { TravelersCheckSchema, STATE_MAP } from "../models/travelersCheck"
import type { TravelersCheck } from "../models/travelersCheck"
import { useUser } from "../../user/hooks/useUser"
import { PageLayout, PageHeader } from "../../../components/crud"
import TravelersCheckDrawer from "./TravelersCheckDrawer"
import type { DrawerMode } from "./TravelersCheckDrawer"

export default function TravelersCheckList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageTravelerChecks ?? false

  const { data, isLoading, page, pageSize, sort, sortDir, handleSortChange, handlePageChange, filters, updateFilters } =
    useDataTable<TravelersCheck>({
      queryKey: "travelers-checks",
      url: "travelers-check",
      schema: TravelersCheckSchema,
      defaultSort: { field: "id", dir: "desc" },
      defaultPageSize: 50,
    })

  const { data: agencies } = useCheckAgencies()
  const deleteMutation = useDeleteTravelersCheck()
  const requestRedeemFormMut = useRequestRedeemForm()
  const redeemMutation = useRedeem()

  const handleRedeem = (id: number) => {
    modals.openConfirmModal({
      title: t("travelersChecks.markRedeemed"),
      children: <Text size="sm">{t("travelersChecks.confirmRedeemMessage")}</Text>,
      labels: { confirm: t("travelersChecks.markRedeemed"), cancel: t("common.cancel") },
      confirmProps: { color: "green" },
      onConfirm: () => redeemMutation.mutate(id),
    })
  }

  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const [selectedCheckId, setSelectedCheckId] = useState<number | null>(null)

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedCheckId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedCheckId(null)
  }, [])

  const handleDeleted = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])

  const [redeemModalOpened, { open: openRedeemModal, close: closeRedeemModal }] = useDisclosure(false)
  const [redeemCheckId, setRedeemCheckId] = useState<number | undefined>(undefined)
  const [redeemIssuingFile, setRedeemIssuingFile] = useState<number | undefined>(undefined)
  const [redeemRedeemFile, setRedeemRedeemFile] = useState<number | undefined>(undefined)

  const handleRequestRedeem = () => {
    if (!redeemCheckId || !redeemIssuingFile || !redeemRedeemFile) return
    requestRedeemFormMut.mutate(
      { travel_check_id: redeemCheckId, issuing_file: redeemIssuingFile, redeem_file: redeemRedeemFile },
      {
        onSuccess: () => {
          notifications.show({ color: "green", title: t("common.success"), message: t("travelersChecks.requestRedeemSuccess") })
          closeRedeemModal()
          setRedeemCheckId(undefined)
          setRedeemIssuingFile(undefined)
          setRedeemRedeemFile(undefined)
        },
        onError: (err) => {
          notifications.show({ color: "red", title: t("common.error"), message: err.message })
        },
      },
    )
  }

  const handleDelete = (id: number) => {
    modals.openConfirmModal({
      title: t("travelersChecks.confirmDelete"),
      children: <Text size="sm">{t("travelersChecks.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(id),
    })
  }

  const columns: Column<TravelersCheck>[] = [
    { key: "id", label: "ID", sortable: true, width: 60 },
    { key: "issuing_agency_name", label: t("travelersChecks.issuingAgency"), sortable: false },
    { key: "redeem_agency_name", label: t("travelersChecks.redeemAgency"), sortable: false },
    { key: "client_name", label: t("travelersChecks.clientName"), sortable: true, width: 140 },
    {
      key: "issuing_file",
      label: t("travelersChecks.issuingFile"),
      sortable: true,
      width: 100,
      render: (v) => v.issuing_file?.toString() ?? "-",
    },
    {
      key: "valid_until",
      label: t("travelersChecks.validUntil"),
      sortable: true,
      width: 100,
      render: (v) => v.valid_until ? new Date(v.valid_until).toLocaleDateString("pt-PT") : "-",
    },
    {
      key: "state",
      label: t("travelersChecks.state"),
      sortable: true,
      width: 100,
      render: (v) => (
        <Badge color={STATE_MAP[v.state]?.color ?? "gray"} variant="light" size="sm">
          {t(`travelersChecks.${v.state}`)}
        </Badge>
      ),
    },
    {
      key: "value",
      label: t("travelersChecks.value"),
      sortable: true,
      width: 90,
      render: (v) => (v.value != null ? `${v.value.toFixed(2)} €` : "-"),
    },
  ]

  const agencyOptions = (agencies ?? []).map((a: { id: number; name: string }) => ({
    value: String(a.id),
    label: a.name,
  }))

  const stateOptions = [
    { value: "created", label: t("travelersChecks.created") },
    { value: "issued", label: t("travelersChecks.issued") },
    { value: "request_redeem", label: t("travelersChecks.request_redeem") },
    { value: "reddem", label: t("travelersChecks.reddem") },
    { value: "deleted", label: t("travelersChecks.deleted") },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("travelersChecks.title")}
          actions={
            <Group>
              {canManage && (
                <>
                  <Button
                    variant="default"
                    onClick={openRedeemModal}
                    leftSection={<i className="fa fa-credit-card" />}
                  >
                    <Trans i18nKey="travelersChecks.requestRedeemBtn" />
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => downloadExport("travelers-check/export", "cheques_viagem.xlsx", filters as Record<string, string | undefined>)}
                    leftSection={<i className="fa fa-file-excel-o" />}
                  >
                    <Trans i18nKey="common.export" />
                  </Button>
                  <Button onClick={() => openDrawer(null, "create")} leftSection={<i className="fa fa-plus" />}>
                    <Trans i18nKey="travelersChecks.create" />
                  </Button>
                </>
              )}
            </Group>
          }
        />
      }
    >
      <Card withBorder p="sm" radius="md">
        <Group grow align="flex-end">
          <Select
            label={t("travelersChecks.filterIssuingAgency")}
            placeholder={t("travelersChecks.selectIssuingAgency")}
            data={agencyOptions}
            searchable
            clearable
            onChange={(val) => updateFilters({ issuing_agency_id: val })}
          />
          <Select
            label={t("travelersChecks.filterRedeemAgency")}
            placeholder={t("travelersChecks.selectRedeemAgency")}
            data={agencyOptions}
            searchable
            clearable
            onChange={(val) => updateFilters({ redeem_agency_id: val })}
          />
          <TextInput
            label={t("travelersChecks.filterClientName")}
            placeholder={t("travelersChecks.filterClientName")}
            onChange={(e) => updateFilters({ client_name: e.currentTarget.value })}
          />
          <TextInput
            label={t("travelersChecks.filterIssuingFile")}
            placeholder={t("travelersChecks.filterIssuingFile")}
            onChange={(e) => updateFilters({ issuing_file: e.currentTarget.value })}
          />
          <Select
            label={t("travelersChecks.filterState")}
            placeholder={t("travelersChecks.filterState")}
            data={stateOptions}
            clearable
            onChange={(val) => updateFilters({ state: val })}
          />
        </Group>
      </Card>

      <DataTable<TravelersCheck>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("travelersChecks.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={(v) => openDrawer(v.id, "view")}
        actions={(v) => (
          <Group gap={4} justify="flex-end" wrap="nowrap">
            {v.state !== "created" ? (
              <Tooltip label={t("travelersChecks.downloadPdf")}>
                <ActionIcon variant="subtle" size="md" onClick={() => travelersCheckApi.downloadPdf(v.id)}>
                  <i className="fa fa-file-pdf-o" />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label={t("travelersChecks.downloadDisabledMessage")}>
                <ActionIcon variant="subtle" size="md" color="gray" disabled>
                  <i className="fa fa-file-pdf-o" />
                </ActionIcon>
              </Tooltip>
            )}
            {v.state === "issued" || v.state === "request_redeem" ? (
              <Tooltip label={t("travelersChecks.markRedeemed")}>
                <ActionIcon variant="subtle" size="md" color="green" onClick={() => handleRedeem(v.id)}>
                  <i className="fa fa-credit-card" />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label={t("travelersChecks.redeemDisabledMessage")}>
                <ActionIcon variant="subtle" size="md" color="gray" disabled>
                  <i className="fa fa-credit-card" />
                </ActionIcon>
              </Tooltip>
            )}
            {canManage && v.state !== "deleted" && (
              <>
                <Tooltip label={t("common.edit")}>
                  <ActionIcon variant="subtle" size="md" onClick={() => openDrawer(v.id, "edit")}>
                    <i className="fa fa-pencil" />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t("common.delete")}>
                  <ActionIcon variant="subtle" size="md" color="red" onClick={() => handleDelete(v.id)}>
                    <i className="fa fa-trash" />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        )}
      />
      <Modal
        opened={redeemModalOpened}
        onClose={closeRedeemModal}
        title={t("travelersChecks.requestRedeemBtn")}
        centered
      >
        <Stack>
          <NumberInput
            label={t("travelersChecks.checkId")}
            placeholder={t("travelersChecks.checkIdPlaceholder")}
            value={redeemCheckId}
            onChange={(val) => setRedeemCheckId(val ? Number(val) : undefined)}
            hideControls
          />
          <NumberInput
            label={t("travelersChecks.issuingFile")}
            placeholder={t("travelersChecks.issuingFilePlaceholder")}
            value={redeemIssuingFile}
            onChange={(val) => setRedeemIssuingFile(val ? Number(val) : undefined)}
            hideControls
          />
          <NumberInput
            label={t("travelersChecks.redeemFile")}
            placeholder={t("travelersChecks.redeemFilePlaceholder")}
            value={redeemRedeemFile}
            onChange={(val) => setRedeemRedeemFile(val ? Number(val) : undefined)}
            hideControls
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeRedeemModal}>
              <Trans i18nKey="common.cancel" />
            </Button>
            <Button onClick={handleRequestRedeem} loading={requestRedeemFormMut.isPending}>
              <Trans i18nKey="travelersChecks.submitRedeemRequest" />
            </Button>
          </Group>
        </Stack>
      </Modal>

      <TravelersCheckDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedCheckId={selectedCheckId}
        onDeleted={handleDeleted}
        onSwitchToEdit={() => setDrawerMode("edit")}
      />
    </PageLayout>
  )
}
